import React, { useEffect, useMemo, useState } from 'react';
import { auditLogService } from '../services/api';
import './AdminAuditLog.css';

const entityLabels = {
  user: 'Používateľ',
  project: 'Projekt',
  rotation: 'Rotácia',
  vacation_request: 'Voľno',
  document: 'Dokument',
  document_requirement: 'Povinné dokumenty',
  terms: 'Podmienky'
};

const actionLabels = {
  create: 'Vytvorenie',
  update: 'Úprava',
  delete: 'Zmazanie',
  block: 'Blokovanie',
  unblock: 'Odblokovanie',
  approved: 'Schválenie',
  rejected: 'Zamietnutie',
  upload: 'Upload',
  copy_week: 'Kópia týždňa',
  bulk_update: 'Hromadná úprava',
  accept: 'Potvrdenie',
  activate: 'Aktivácia',
  update_requirements: 'Povinné dokumenty'
};

const formatDateTime = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString('sk-SK', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const AdminAuditLog = ({ user }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [filters, setFilters] = useState({ entity_type: '', action: '', limit: 100 });

  const loadAuditLog = async () => {
    try {
      setLoading(true);
      setError(null);
      const params = {
        limit: filters.limit,
        ...(filters.entity_type ? { entity_type: filters.entity_type } : {}),
        ...(filters.action ? { action: filters.action } : {})
      };
      const data = await auditLogService.list(params);
      setItems(data.items || []);
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Chyba pri načítaní audit logu');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAuditLog();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.entity_type, filters.action, filters.limit]);

  const entityOptions = useMemo(() => Object.entries(entityLabels), []);
  const actionOptions = useMemo(() => Object.entries(actionLabels), []);

  if (user?.role !== 'admin') {
    return <div className="audit-log-container error">❌ Audit log je dostupný iba pre admina.</div>;
  }

  return (
    <div className="audit-log-container">
      <div className="audit-log-header">
        <div>
          <h1>🧾 Audit log</h1>
          <p>Prehľad dôležitých zmien v systéme: používatelia, projekty, rotácie, žiadosti, dokumenty a podmienky.</p>
        </div>
        <button type="button" onClick={loadAuditLog}>Obnoviť</button>
      </div>

      <div className="audit-log-filters">
        <label>
          Oblasť
          <select value={filters.entity_type} onChange={(e) => setFilters(prev => ({ ...prev, entity_type: e.target.value }))}>
            <option value="">Všetky oblasti</option>
            {entityOptions.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
          </select>
        </label>
        <label>
          Akcia
          <select value={filters.action} onChange={(e) => setFilters(prev => ({ ...prev, action: e.target.value }))}>
            <option value="">Všetky akcie</option>
            {actionOptions.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
          </select>
        </label>
        <label>
          Limit
          <select value={filters.limit} onChange={(e) => setFilters(prev => ({ ...prev, limit: Number(e.target.value) }))}>
            <option value={50}>50</option>
            <option value={100}>100</option>
            <option value={250}>250</option>
            <option value={500}>500</option>
          </select>
        </label>
      </div>

      {loading ? (
        <div className="audit-log-state">Načítavam audit log...</div>
      ) : error ? (
        <div className="audit-log-state error">❌ {error}</div>
      ) : items.length === 0 ? (
        <div className="audit-log-state">Zatiaľ nie sú dostupné žiadne audit udalosti.</div>
      ) : (
        <div className="audit-log-list">
          {items.map((item) => (
            <article key={item.id} className="audit-log-item">
              <button type="button" className="audit-log-main" onClick={() => setExpandedId(expandedId === item.id ? null : item.id)}>
                <span className={`audit-action ${item.action}`}>{actionLabels[item.action] || item.action}</span>
                <span className="audit-summary">{item.summary}</span>
                <span className="audit-meta">
                  {entityLabels[item.entity_type] || item.entity_type}
                  {item.entity_id ? ` #${item.entity_id}` : ''}
                </span>
                <span className="audit-actor">{item.actor_username || 'Systém'} · {item.actor_role || '—'}</span>
                <span className="audit-time">{formatDateTime(item.created_at)}</span>
              </button>
              {expandedId === item.id && (
                <div className="audit-details">
                  <div className="audit-details-grid">
                    <div><strong>IP</strong><span>{item.ip_address || '—'}</span></div>
                    <div><strong>Prehliadač</strong><span>{item.user_agent || '—'}</span></div>
                  </div>
                  <pre>{JSON.stringify(item.details || {}, null, 2)}</pre>
                </div>
              )}
            </article>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminAuditLog;
